/**
 * DeadStarSkillEffect — apotheosis_event_horizon (Astral rift-path apotheosis).
 *
 * Places a massive gravity well at a targeted position (up to 5 tiles from the
 * player). The well persists for 8 seconds, pulling all enemies within 5 tiles
 * toward its center. While active, every enemy in the pull radius takes
 * distance-scaled damage per tick: 3/sec at the edge, 12/sec at center, linearly
 * interpolated. Unlike Event Horizon, the player stands outside — the collapse
 * happens at range.
 *
 * Phase sequence: active (8000ms) → cooldown (30000ms — very_long apotheosis).
 *
 * OR-gate unlock: SkillProgressionComponent checks
 *   `parentIds.some(id => unlockedNodes.has(id))` for this node's parentIds
 *   array (`["event_horizon_passive", "anchor_passive"]`). Dead Star is
 *   reachable via either the Event Horizon sub-path or the Anchor sub-path.
 *
 * Attunement cost: 1 slot (all active skills, apotheosis included, cost exactly 1
 * slot — design-decisions.md §1. The JSON description says "2 slots" but that text
 * is incorrect per the V4 design doc; do not implement 2-slot cost.)
 *
 * Design values (from design-decisions.md § Astral Flags):
 * - Proximity damage: 3/sec edge → 12/sec center (linear)
 * - Placement range: 5 tiles
 * - Pull radius: 5 tiles
 * - Duration: 8000ms
 * - Cooldown: 30000ms (very_long apotheosis tier — not specified in JSON; 30s
 *   is a design resolution for the "Very long cooldown" notation)
 * - Pull strength: 3 tiles/sec
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § apotheosis_event_horizon
 * @see design-decisions.md § Astral Flags (Dead Star)
 * @see ADR-064 (attunement shared systems architecture)
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';
import type { GravityWellConfig } from '../../../systems/GravityPullSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Minimal zone config needed for Dead Star's spatial zone registration. */
export interface DeadStarZoneConfig {
  id: string;
  type: string;
  sourceSkillId: string;
  shape: 'circle';
  position: { x: number; y: number };
  radiusTiles: number;
  durationMs: number;
}

export interface DeadStarDeps {
  readonly gravityPull: {
    addWell(config: GravityWellConfig): string;
    removeWell(wellId: string): void;
  };
  readonly zones: {
    create(config: DeadStarZoneConfig): string;
    remove(zoneId: string): void;
  };
  readonly enemies: {
    /** Returns IDs of enemies whose center lies within radiusTiles of position. */
    getEnemiesInRadius(position: { x: number; y: number }, radiusTiles: number): string[];
    /** Returns the current pixel-space position of the enemy, or null if not found. */
    getEnemyPosition(enemyId: string): { x: number; y: number } | null;
  };
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  readonly targeting: {
    getTargetPosition(): { x: number; y: number };
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DeadStarConfig {
  readonly wellDurationMs: number;
  readonly cooldownMs: number;
  readonly pullRadiusTiles: number;
  readonly pullStrengthTilesPerSec: number;
  readonly placementRangeTiles: number;
  /** Damage per second dealt to an enemy at the pull radius edge (minimum proximity). */
  readonly edgeDamagePerSec: number;
  /** Damage per second dealt to an enemy at the exact center (maximum proximity). */
  readonly centerDamagePerSec: number;
}

const DEFAULT_CONFIG: DeadStarConfig = {
  wellDurationMs: 8000,
  cooldownMs: 30000,      // very_long apotheosis tier
  pullRadiusTiles: 5,
  pullStrengthTilesPerSec: 3,
  placementRangeTiles: 5,
  edgeDamagePerSec: 3,
  centerDamagePerSec: 12,
};

import { TILE_SIZE } from '../../../utils/Constants.js';


// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DeadStarSkillEffect extends PhasedSkillEffect {
  override readonly skillId = 'apotheosis_event_horizon';

  private readonly deps: DeadStarDeps;
  readonly config: DeadStarConfig;

  private _wellId: string | null = null;
  private _zoneId: string | null = null;
  /**
   * Pixel-space position of the currently-active gravity well.
   * Set in onPhaseEnter('active'), cleared in _closeWell().
   */
  private _wellPosition: { x: number; y: number } | null = null;

  private _activationsCount = 0;
  /** Cumulative damage dealt since construction — used in simulation assertions. */
  private _totalDamageDealt = 0;
  /** Number of update() ticks that dealt at least one point of damage. */
  private _tickCount = 0;

  constructor(deps: DeadStarDeps, config: Partial<DeadStarConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get activationsCount(): number { return this._activationsCount; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get tickCount(): number { return this._tickCount; }
  get activeWellId(): string | null { return this._wellId; }
  get wellPosition(): Readonly<{ x: number; y: number }> | null { return this._wellPosition; }

  // --- PhasedSkillEffect implementation ---

  protected override readonly phaseSequence: readonly SkillPhase[] = ['active', 'cooldown'];

  protected override getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'active':   return this.config.wellDurationMs;
      case 'cooldown': return this.config.cooldownMs;
      default:         return 0;
    }
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._openWell();
    } else if (phase === 'cooldown') {
      this._closeWell();
    }
  }

  /**
   * Per-frame damage tick — only fires during the active phase.
   *
   * Queries all enemies within the pull radius and applies distance-scaled
   * damage per tick. The damage rate scales linearly between edgeDamagePerSec
   * (at the pull radius boundary) and centerDamagePerSec (at distance 0):
   *
   *   damagePerSec = edgeDps + (centerDps - edgeDps) * (1 − distPx / pullRadiusPx)
   *
   * This is the linear form of the design-decisions "3/sec edge → 12/sec center"
   * specification. An enemy exactly at the well center takes 12/sec; one at the
   * pull boundary takes 3/sec; one midway takes 7.5/sec.
   */
  protected override onUpdate(delta: number): void {
    if (this.phase !== 'active' || this._wellPosition === null) return;

    const enemies = this.deps.enemies.getEnemiesInRadius(
      this._wellPosition,
      this.config.pullRadiusTiles,
    );
    if (enemies.length === 0) return;

    const pullRadiusPx = this.config.pullRadiusTiles * TILE_SIZE;
    const edgeDps = this.config.edgeDamagePerSec;
    const damageRange = this.config.centerDamagePerSec - edgeDps;
    let ticked = false;

    for (const enemyId of enemies) {
      const pos = this.deps.enemies.getEnemyPosition(enemyId);
      if (pos === null) continue;

      const dx = pos.x - this._wellPosition.x;
      const dy = pos.y - this._wellPosition.y;
      const distPx = Math.sqrt(dx * dx + dy * dy);

      // t ∈ [0, 1]: 0 at edge, 1 at center.
      // Clamp to [0, 1] to guard against floating-point overshoot when enemy
      // position is returned very slightly beyond the pull radius.
      const t = Math.max(0, Math.min(1, 1 - distPx / pullRadiusPx));
      const dps = edgeDps + damageRange * t;
      const tickDamage = dps * (delta / 1000);

      this.deps.combat.dealDamage(enemyId, tickDamage);
      this._totalDamageDealt += tickDamage;
      ticked = true;
    }

    if (ticked) this._tickCount++;
  }

  // --- Internal helpers ---

  private _openWell(): void {
    const from = this.deps.player.getPosition();
    const target = this.deps.targeting.getTargetPosition();
    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxRangePx = this.config.placementRangeTiles * TILE_SIZE;

    // Clamp target to placement range along the aim direction.
    let tx: number;
    let ty: number;
    if (dist <= 0.001 || dist <= maxRangePx) {
      tx = target.x;
      ty = target.y;
    } else {
      tx = from.x + (dx / dist) * maxRangePx;
      ty = from.y + (dy / dist) * maxRangePx;
    }

    this._wellPosition = { x: tx, y: ty };

    // Register gravity well (GravityPullSystem handles expiry at wellDurationMs).
    const wellId = `dead_star_well_${this._activationsCount}`;
    this.deps.gravityPull.addWell({
      id: wellId,
      position: { ...this._wellPosition },
      pullRadiusTiles: this.config.pullRadiusTiles,
      pullStrengthTilesPerSec: this.config.pullStrengthTilesPerSec,
      durationMs: this.config.wellDurationMs,
      sourceSkillId: 'apotheosis_event_horizon',
    });
    this._wellId = wellId;

    // Register a Dead Star spatial zone for external queries
    // (e.g. other skills checking isInsideType('dead_star')).
    const zoneId = `dead_star_zone_${this._activationsCount}`;
    this.deps.zones.create({
      id: zoneId,
      type: 'dead_star',
      sourceSkillId: 'apotheosis_event_horizon',
      shape: 'circle',
      position: { ...this._wellPosition },
      radiusTiles: this.config.pullRadiusTiles,
      durationMs: this.config.wellDurationMs,
    });
    this._zoneId = zoneId;

    this._activationsCount++;
  }

  private _closeWell(): void {
    if (this._wellId !== null) {
      this.deps.gravityPull.removeWell(this._wellId);
      this._wellId = null;
    }
    if (this._zoneId !== null) {
      this.deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this._wellPosition = null;
  }

  override destroy(): void {
    this._closeWell();
    super.destroy();
  }
}
